import { useQuery } from '@tanstack/react-query';
import { integrationsApi } from '@/api/integrations';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { IntegrationKind, IntegrationsView } from '@/types/api';

export function useIntegrations(enabled = true) {
  return useQuery({ queryKey: queryKeys.integrations, queryFn: integrationsApi.list, enabled });
}

/**
 * One integration's page (ADR-0052), asked again every half minute: the
 * worker checks keys and reads sources on its own, and the page should say so.
 */
export function useIntegration(kind: IntegrationKind) {
  return useQuery({ queryKey: queryKeys.integration(kind), queryFn: () => integrationsApi.detail(kind), refetchInterval: 30_000 });
}

/** What becomes available with a key: Search Console, SEO data, the writer. */
const affected = () => [queryKeys.integrations, queryKeys.searchConsole.network, queryKeys.seoMetrics.network, queryKeys.content.all];

export function useSaveIntegration(kind: IntegrationKind, callbacks?: MutationCallbacks<IntegrationsView, Record<string, unknown>>) {
  return useApiMutation((input: Record<string, unknown>) => integrationsApi.save(kind, input), affected, callbacks);
}

export function useCheckIntegration(kind: IntegrationKind, callbacks?: MutationCallbacks<IntegrationsView, void>) {
  return useApiMutation(() => integrationsApi.check(kind), () => [queryKeys.integrations], callbacks);
}

export function useRemoveIntegration(kind: IntegrationKind, callbacks?: MutationCallbacks<{ removed: boolean }, void>) {
  return useApiMutation(() => integrationsApi.remove(kind), affected, callbacks);
}
